/**
 * Base API Client
 * Core HTTP client for communicating with the Trading API backend via same-origin BFF.
 *
 * Auth model (preferred):
 *   Browser → same-origin `/api/v1/*` with credentials: 'include'
 *   BFF attaches Authorization from session cookies and forwards CSRF to QTP.
 *
 * CSRF (QTP Fiber double-submit):
 *   Cookie `_csrf` (readable) + header `X-CSRF-Token` on POST/PUT/PATCH/DELETE.
 *   Bootstrap via GET that is not CSRF-skipped so Fiber mints the cookie.
 *
 * If NEXT_PUBLIC_API_BASE_URL points at api.sandbox (cross-origin) instead of
 * empty same-origin BFF: cookies + CSRF require CORS Allow-Credentials and
 * matching cookie Domain — prefer leaving baseUrl empty.
 */

import {
  ApiConfig,
  ApiError,
  ApiResponse,
  DEFAULT_API_CONFIG,
  HTTP_METHODS,
  getAuthHeaders,
  getDefaultHeaders,
} from './config';
import { handleApiError } from '@/shared/utils/api/api-error-handler';
import { shouldDispatchSessionExpired } from './session-expiry';

const CSRF_COOKIE = '_csrf';
const CSRF_HEADER = 'X-CSRF-Token';
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Soft-fail 401 copy for order/trading mutations (no forced redirect). */
export const AUTH_REQUIRED_MESSAGE =
  'Authentication required. Sign in again to continue trading.';

function readCookie(name: string): string {
  if (typeof document === 'undefined') return '';
  const prefix = `${name}=`;
  const found = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return found ? decodeURIComponent(found.slice(prefix.length)) : '';
}

function isMutatingMethod(method: string): boolean {
  return MUTATING_METHODS.has(method.toUpperCase());
}

function isCsrfFailure(status: number, errorData: Partial<ApiError> | null): boolean {
  if (status !== 403) return false;
  const code = String((errorData as any)?.error || (errorData as any)?.code || '').toUpperCase();
  const msg = String((errorData as any)?.message || '').toLowerCase();
  return code.includes('CSRF') || msg.includes('csrf');
}

export class BaseApiClient {
  private config: ApiConfig;
  private authToken?: string;
  /** In-memory CSRF token cache (browser only). */
  private csrfToken?: string;
  private csrfBootstrapPromise: Promise<string | undefined> | null = null;

  constructor(config: Partial<ApiConfig> = {}) {
    this.config = { ...DEFAULT_API_CONFIG, ...config };
  }

  setAuthToken(token: string): void {
    this.authToken = token;
  }
  clearAuthToken(): void {
    this.authToken = undefined;
  }

  /**
   * Ensure a QTP CSRF token is available for mutating requests.
   * Prefer cookie → cache → bootstrap GET (mints `_csrf` via Fiber).
   */
  private async ensureCsrfToken(forceRefresh = false): Promise<string | undefined> {
    if (typeof window === 'undefined') return undefined;

    if (!forceRefresh) {
      const fromCookie = readCookie(CSRF_COOKIE);
      if (fromCookie) {
        this.csrfToken = fromCookie;
        return fromCookie;
      }
      if (this.csrfToken) return this.csrfToken;
    } else {
      this.csrfToken = undefined;
    }

    if (this.csrfBootstrapPromise && !forceRefresh) {
      return this.csrfBootstrapPromise;
    }

    this.csrfBootstrapPromise = this.bootstrapCsrfToken().finally(() => {
      this.csrfBootstrapPromise = null;
    });
    return this.csrfBootstrapPromise;
  }

  private async bootstrapCsrfToken(): Promise<string | undefined> {
    const base = this.config.baseUrl || '';
    // Prefer documented endpoint; if QTP skips CSRF middleware there, fall back
    // to a non-skipped safe GET so Fiber mints `_csrf`.
    // /api/v1/csrf documents the contract (may skip Fiber CSRF mint).
    // GET /api/v1/trading/orders is non-skipped after BFF rewrite → QTP /api/v1/orders.
    const bootstrapPaths = ['/api/v1/csrf', '/api/v1/trading/orders'];

    for (const path of bootstrapPaths) {
      try {
        await fetch(`${base}${path}`, {
          method: 'GET',
          credentials: 'include',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
      } catch {
        // Network errors: try next path
      }
      const token = readCookie(CSRF_COOKIE);
      if (token) {
        this.csrfToken = token;
        return token;
      }
    }
    return this.csrfToken;
  }

  async request<T = any>(
    method: string,
    endpoint: string,
    data?: any,
    options: { headers?: Record<string, string>; timeout?: number; retryAttempts?: number } = {}
  ): Promise<ApiResponse<T>> {
    const {
      headers = {},
      timeout = this.config.timeout,
      retryAttempts = this.config.retryAttempts,
    } = options;
    const url = `${this.config.baseUrl}${endpoint}`;
    let lastError: ApiError | null = null;
    let csrfRetried = false;

    for (let attempt = 0; attempt <= retryAttempts; attempt++) {
      try {
        const requestHeaders: Record<string, string> = this.authToken
          ? { ...getAuthHeaders(this.authToken), ...headers }
          : { ...getDefaultHeaders(), ...headers };

        // Mutating calls: attach double-submit CSRF (browser → same-origin BFF → QTP)
        if (typeof window !== 'undefined' && isMutatingMethod(method)) {
          const csrf = await this.ensureCsrfToken(csrfRetried);
          if (csrf) {
            requestHeaders[CSRF_HEADER] = csrf;
          }
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('BaseApiClient: Making request to:', url, method);
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        const requestOptions: RequestInit = {
          method,
          headers: requestHeaders,
          signal: controller.signal,
          // Session cookies + `_csrf` for BFF / QTP. Same-origin preferred.
          credentials: 'include',
        };
        if (data && method !== HTTP_METHODS.GET) {
          requestOptions.body = JSON.stringify(data);
        }

        const response = await fetch(url, requestOptions);
        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await this.parseErrorResponse(response);

          // One automatic CSRF recovery (cookie raced or expired)
          if (
            !csrfRetried &&
            typeof window !== 'undefined' &&
            isMutatingMethod(method) &&
            isCsrfFailure(response.status, errorData)
          ) {
            csrfRetried = true;
            await this.ensureCsrfToken(true);
            attempt -= 1; // do not consume a network retry for CSRF refresh
            continue;
          }

          const softAuthMessage =
            response.status === 401
              ? (errorData as any).message ||
              (errorData as any).detail ||
              AUTH_REQUIRED_MESSAGE
              : undefined;

          const apiError = {
            message:
              softAuthMessage ||
              (errorData as any).message ||
              (errorData as any).detail ||
              `HTTP ${response.status}`,
            status: response.status,
            code: (errorData as any).code || (errorData as any).error,
            details: (errorData as any).details || (errorData as any).detail,
          } as ApiError;

          // Session expiry → AuthProvider auto sign-out.
      // Portal cookie ≠ QTP OIDC. Generic unauthorized on desk APIs must not logout.
      if (
        response.status === 401 &&
        typeof window !== 'undefined' &&
        shouldDispatchSessionExpired({
          status: response.status,
          code: (errorData as { code?: string; error?: string } | null)?.code
            || (errorData as { error?: string } | null)?.error,
          message:
            (errorData as { message?: string; detail?: string } | null)?.message
            || (errorData as { detail?: string } | null)?.detail,
          endpoint,
        })
      ) {
        window.dispatchEvent(
          new CustomEvent('session-expired', {
            detail: {
              message:
                apiError.message ||
                'Your session has expired. Please sign in again.',
              timestamp: new Date().toISOString(),
            },
          })
        )
      }

      throw apiError;
        }

        const responseData = await this.parseResponse<T>(response);
        // After successful responses, refresh CSRF cache from cookie if present
        if (typeof window !== 'undefined') {
          const cookieToken = readCookie(CSRF_COOKIE);
          if (cookieToken) this.csrfToken = cookieToken;
        }
        return { data: responseData, status: response.status, success: true };
      } catch (error) {
        lastError = this.handleRequestError(error);

        if (attempt === retryAttempts) {
          handleApiError(error, {
            context: `API Request: ${method} ${endpoint}`,
            showToast: false,
            logError: true,
          });
        }

        // Do not retry hard client errors except rate limit
        if (lastError.status >= 400 && lastError.status < 500 && lastError.status !== 429) {
          break;
        }
        if (attempt === retryAttempts) break;
        await this.delay(this.config.retryDelay * Math.pow(2, attempt));
      }
    }

    const finalError = lastError || {
      message: 'Request failed after all retry attempts',
      status: 500,
    };
    throw finalError;
  }

  async get<T = any>(
    endpoint: string,
    options?: { headers?: Record<string, string>; timeout?: number }
  ): Promise<ApiResponse<T>> {
    return this.request<T>(HTTP_METHODS.GET, endpoint, undefined, options);
  }
  async post<T = any>(
    endpoint: string,
    data?: any,
    options?: { headers?: Record<string, string>; timeout?: number }
  ): Promise<ApiResponse<T>> {
    return this.request<T>(HTTP_METHODS.POST, endpoint, data, options);
  }
  async put<T = any>(
    endpoint: string,
    data?: any,
    options?: { headers?: Record<string, string>; timeout?: number }
  ): Promise<ApiResponse<T>> {
    return this.request<T>(HTTP_METHODS.PUT, endpoint, data, options);
  }
  async delete<T = any>(
    endpoint: string,
    options?: { headers?: Record<string, string>; timeout?: number }
  ): Promise<ApiResponse<T>> {
    return this.request<T>(HTTP_METHODS.DELETE, endpoint, undefined, options);
  }
  async patch<T = any>(
    endpoint: string,
    data?: any,
    options?: { headers?: Record<string, string>; timeout?: number }
  ): Promise<ApiResponse<T>> {
    return this.request<T>(HTTP_METHODS.PATCH, endpoint, data, options);
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) return await response.json();
    if (contentType?.includes('text/')) {
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch {
        return text as unknown as T;
      }
    }
    return (await response.blob()) as unknown as T;
  }

  private async parseErrorResponse(response: Response): Promise<Partial<ApiError>> {
    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return await response.json();
      }

      const textContent = await response.text();
      try {
        return JSON.parse(textContent);
      } catch {
        return { message: textContent || `HTTP ${response.status}` };
      }
    } catch {
      return { message: `HTTP ${response.status}` };
    }
  }

  private handleRequestError(error: any): ApiError {
    if ((error as any).name === 'AbortError') {
      return { message: 'Request timeout', status: 408, code: 'TIMEOUT' };
    }

    if (error instanceof TypeError && (error as any).message?.includes('fetch')) {
      return {
        message: 'Network error - unable to connect to server',
        status: 0,
        code: 'NETWORK_ERROR',
      };
    }

    if ((error as any).status) {
      return error as ApiError;
    }

    if (error instanceof Error) {
      return {
        message: error.message || 'Unknown error occurred',
        status: 500,
        code: 'UNKNOWN_ERROR',
        details: error.stack,
      };
    }

    if (typeof error === 'string') {
      return { message: error, status: 500, code: 'UNKNOWN_ERROR' };
    }

    if (typeof error === 'object' && error !== null) {
      return {
        message: (error as any).message || 'Unknown error occurred',
        status: (error as any).status || 500,
        code: (error as any).code || 'UNKNOWN_ERROR',
        details: JSON.stringify(error),
      };
    }

    return {
      message: 'Unknown error occurred',
      status: 500,
      code: 'UNKNOWN_ERROR',
      details: String(error),
    };
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * QTP system health (public on backend). Prefer /api/v1/health so BFF can probe QTP
   * without attaching portal session Bearer (dev-agent/session JWTs are not OIDC access
   * tokens and previously caused 401 "invalid token", stranding the dashboard on Connecting…).
   */
  async healthCheck(): Promise<ApiResponse> {
    return this.get('/api/v1/health');
  }
  async getSystemInfo(): Promise<ApiResponse> {
    return this.get('/');
  }
}
